import React, { useMemo, useState } from "react";
import { NextPage } from "next";
import { useLazyQuery, useMutation } from "@apollo/react-hooks";
import { useUserContext } from "../contexts/UserContext";
import { withApollo } from "../components/WithApollo";
import useFormHook from "../hooks/useForm";
import { useRouter } from "next/router";
import gql from "graphql-tag";
import { Button } from "@/components/tailwind-ui/Button";
import { Input } from "@/components/tailwind-ui/Input";
import { getThemeFromDomain } from "./_app";
import { CREATE_USER } from "./repair/order";
import { Select } from "@/components/tailwind-ui/Select";

const inputColors = {
  invalid: "border-red-400 placeholder-red-500",
  default: "border-gray-300 placeholder-gray-500",
};

const LOGIN = gql`
  mutation MyMutation($email: String!) {
    login(email: $email) {
      __typename
      success
    }
  }
`;

const Register: NextPage = (props: any) => {
  console.log(props);
  const { theme } = props;
  const router = useRouter();
  const { userData } = useUserContext();

  const [createUser, createUserResponse] = useMutation(CREATE_USER);
  const [success, setSuccess] = useState(false);
  const showResult = createUserResponse.called && !createUserResponse.loading;
  const userAlreadyExists = createUserResponse.error?.graphQLErrors.some(
    (e) => e.message === 'Uniqueness violation. duplicate key value violates unique constraint "user_email_key"'
  );
  if (userData) {
    router.push("/");
    return null;
  }

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const variables = Object.fromEntries(new FormData(e.currentTarget));
    const belongsTo = getThemeFromDomain(location.host);
    const isDesos = belongsTo === "desos";
    const isColosseum = belongsTo === "colosseum";
    try {
      // @ts-ignore
      await createUser({ variables: { ...variables, isDesos, isColosseum, email: variables.email.trim() } });
      setSuccess(true);
    } catch (error) {
      console.log(error);
    }
  };
  if (showResult && success) {
    return (
      <div className="flex flex-col items-center justify-center min-h-full my-auto">
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="px-4 py-8 bg-white shadow sm:rounded-lg sm:px-10">
            Ihr Account wurde erstellt. Sie können sich nun über folgenden Link einloggen
            <br />
            <a href="/login" className="underline bg-white text-primary">
              Hier klicken zum Einloggen
            </a>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center min-h-full my-auto">
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
        <div className="px-4 py-8 bg-white shadow sm:rounded-lg sm:px-10">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            {theme === "colosseum" && (
              <div className="text-right text-gray-700">
                <img src="/Colosseum Logo.svg" alt="Colosseum Dental Logo" className="w-auto h-12 mx-auto" />
                <span>
                  powered by <span className="font-semibold">Desos</span>
                </span>
              </div>
            )}
            {theme === "winkelstueck-reparatur" && (
              <img src="/Winkelstück Reparatur.jpg" alt="Winkelstück Reparatur Logo" className="w-auto h-12 mx-auto" />
            )}
            {theme === "desos" && <img src="/desos/Logo_türkis.svg" alt="Desos Logo" className="w-auto h-12 mx-auto" />}
          </div>
          <form className="mt-8 space-y-6" onSubmit={onSubmit}>
            <div className="grid grid-cols-2 gap-2">
              <Input
                id="office"
                name="office"
                label="Praxisname"
                placeholder="Praxisname"
                required
                className="col-span-2"
              />
              <Input id="firstName" name="firstName" label="Vorname" placeholder="Vorname" required />
              <Input id="lastName" name="lastName" label="Nachname" placeholder="Nachname" required />
              <Input label="E-Mail" required name="email" type="email" autoComplete="email" placeholder="E-Mail" />

              <Input id="phone" name="phone" label="Telefon" placeholder="Telefon" required />
              <Input
                id="street"
                name="street"
                label="Straße und Hausnummer"
                placeholder="Straße und Hausnummer"
                required
              />
              <Input id="zip" name="zip" label="Postleitzahl" placeholder="Postleitzahl" required />
              <Input id="city" name="city" label="Stadt" placeholder="Stadt" required />

              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                  Land auswählen *
                </label>
                <select
                  id="country"
                  name="country"
                  className="block w-full py-2 pl-3 pr-10 mt-1 text-base border-gray-300 rounded-md focus:outline-none focus:ring-primary-dark focus:border-primary-dark sm:text-sm"
                  required
                  value="DE"
                >
                  <option value="">Bitte auswählen</option>
                  <option value="DE">Deutschland</option>
                  <option value="AT">Österreich</option>
                  <option value="CH">Schweiz</option>
                  <option value="LI">Liechtenstein</option>
                  <option value="IT">Italien</option>
                  <option value="FR">Frankreich</option>
                  <option value="NL">Niederlande</option>
                </select>
              </div>
              <div className="col-span-2">
                <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                  Wie sind Sie auf uns aufmerksam geworden?
                </label>
                <select
                  id="country"
                  name="country"
                  className="block w-full py-2 pl-3 pr-10 mt-1 text-base border-gray-300 rounded-md focus:outline-none focus:ring-primary-dark focus:border-primary-dark sm:text-sm"
                >
                  <option value="">Bitte auswählen</option>
                  <option>Über eine Empfehlung</option>
                  <option>Über Google-Werbung</option>
                  <option>Über eine Werbeanzeige</option>
                  <option>Eine Veranstaltung/Messe</option>
                  <option>Social Media</option>
                  <option>Bereits Kunde</option>
                  <option>Sonstiges</option>
                </select>
              </div>
            </div>

            <Button type="submit" scheme="primary" size="2" className="flex justify-center w-full">
              Registrieren
            </Button>
            {userAlreadyExists && <p className="mt-2 text-sm text-red-600">Diese E-Mail ist bereits registriert</p>}
          </form>
        </div>
      </div>
    </div>
  );
};

export default withApollo()(Register);
